import * as React from "react";

import { formatDuration } from "@/components/runs/run-duration";

/**
 * **Time since a run was submitted, ticking (VIS/V1).**
 *
 * ---
 *
 * ## This is a DIFFERENT figure from the header's elapsed, on purpose
 *
 * `run-header-meta.ts::elapsedLabel` renders wall time between `started_at` and
 * `finished_at`, and refuses to render anything unless BOTH exist — because
 * "duration" has no single meaning across the lifecycle and it would rather
 * omit the field than mislabel one. That guard is right and is untouched.
 *
 * It also means an in-flight run has no elapsed figure at all there, which is
 * exactly the field a live run view needs. So this computes a strictly weaker,
 * strictly well-defined quantity: **wall time since `submitted_at`**, the one
 * timestamp the run detail payload always carries and always populates
 * (`server/routes/runs.py` sends `record.created_at`).
 *
 * The two are not interchangeable and the copy never calls this one "elapsed":
 * time since submission includes any queueing before the run started, so it is
 * an upper bound on execution time, not a measure of it. The label at the call
 * site is "Since submitted" for that reason.
 *
 * ## Ticking is local, and costs no requests
 *
 * A run detail page already polls (`useRun`, 5s). This deliberately does NOT
 * ride that poll: a clock that advanced in 5-second jumps would read as a
 * stalled UI. It is a 1s `setInterval` over local state, it issues no fetch, it
 * touches no query cache, and it stops the moment `live` goes false — so a
 * settled run leaves no timer behind.
 */

/**
 * `submittedAt` → now, formatted, or `null` when the arithmetic would be a
 * claim the data does not support.
 *
 * `null` for an unparseable timestamp and for a NEGATIVE span — a clock skew
 * between the server's `created_at` and the browser's `Date.now()` can put
 * submission in the future, and "-3.2s since submitted" is worse than no field.
 * Kept pure and separate from the hook so both are testable without timers.
 */
export function sinceSubmittedLabel(submittedAt: string, nowMs: number): string | null {
  const submitted = Date.parse(submittedAt);
  if (Number.isNaN(submitted)) return null;
  const seconds = (nowMs - submitted) / 1000;
  if (seconds < 0) return null;
  return formatDuration(seconds);
}

/** Milliseconds between ticks. One second: the smallest step
 * `formatDuration` can render (`12.3s`) is a tenth, but a 100ms clock is
 * motion for its own sake and the figure is read, not watched. */
export const TICK_MS = 1_000;

/**
 * {@link sinceSubmittedLabel} against a clock that advances while `live`.
 *
 * Recomputed once on every `live`/`submittedAt` change so the first paint is
 * correct rather than a second stale.
 */
export function useSinceSubmitted(submittedAt: string, live: boolean): string | null {
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  React.useEffect(() => {
    setNowMs(Date.now());
    if (!live) return;
    const id = setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [live, submittedAt]);

  return sinceSubmittedLabel(submittedAt, nowMs);
}
